'use client'

import { useCallback, useRef, useState, useSyncExternalStore } from 'react'
import { mergeTranscript } from './voice-transcript'
import { spokenNumbersToTr } from './spoken-numbers-tr'

// Web Speech API — не часть TS lib.dom, официальных типов нет. Минимальные
// типы под то, что реально используется (браузерная реализация STT для
// голосового ввода вопроса, тикет .scratch/voice-ai-tutor/issues/01).
type SpeechRecognitionAlternative = { transcript: string }
type SpeechRecognitionResult = { isFinal: boolean; length: number; [index: number]: SpeechRecognitionAlternative }
type SpeechRecognitionResultList = { length: number; [index: number]: SpeechRecognitionResult }
type SpeechRecognitionEvent = { resultIndex: number; results: SpeechRecognitionResultList }
type SpeechRecognitionErrorEvent = { error: string }

interface SpeechRecognitionLike {
  lang: string
  interimResults: boolean
  continuous: boolean
  start(): void
  stop(): void
  onresult: ((e: SpeechRecognitionEvent) => void) | null
  onerror: ((e: SpeechRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognitionLike
    webkitSpeechRecognition?: new () => SpeechRecognitionLike
  }
}

// STT-провайдер сейчас — браузерный Web Speech API (бесплатно, без бэкенда,
// без аккаунта). Временное решение на время MVP: план — заменить на Yandex
// SpeechKit, когда будет ключ (см. .scratch/voice-ai-tutor/spec.md,
// "Явно отклонено"). Единственная точка замены — вызов recognition.start()
// ниже; остальной компонент (ai-chat.tsx) от провайдера не зависит.
// Поддержка Web Speech API не меняется за время жизни компонента — внешнего
// события подписки нет, subscribe — no-op. getServerSnapshot всегда false:
// на сервере window нет, а сама фича всё равно нужна только в браузере.
function subscribeNever() {
  return () => {}
}
// Тот же чек, что и внутри хука (isSupported), но как обычная функция —
// нужен в tutor-fab.tsx для синхронной проверки при долгом нажатии, ещё до
// того как TutorQa вообще смонтирована (и значит вызвать хук неоткуда).
export function isVoiceInputSupported() {
  return typeof window !== 'undefined' && !!(window.SpeechRecognition ?? window.webkitSpeechRecognition)
}
function getSupportServerSnapshot() {
  return false
}

// UA-сниффинг тут вынужденный: отличить "движок принимает continuous" от
// "молча ломается на нём" фича-детектом нельзя, а на iOS все браузеры всё
// равно один и тот же WebKit.
const IS_IOS = typeof navigator !== 'undefined' && /iP(hone|ad|od)/.test(navigator.userAgent)

// continuous=true — режим «говорю, пока держу кнопку» (tutor-qa.tsx): без
// него распознавание само обрывается на первой же паузе в середине длинного
// вопроса, и вторая половина фразы теряется. Останавливает запись только
// stop() по отпусканию кнопки. В диалоге-практике (ai-chat.tsx) реплики
// короткие — там остаётся прежнее автозавершение по паузе.
export function useVoiceInput(lang = 'tr-TR', continuous = false) {
  const isSupported = useSyncExternalStore(subscribeNever, isVoiceInputSupported, getSupportServerSnapshot)
  const [isListening, setIsListening] = useState(false)
  const [interimText, setInterimText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  // Накопленные окончательные куски за одну запись. В continuous-режиме их
  // приходит несколько (по одному на фразу между паузами), и каждый следующий
  // должен дописываться к предыдущим, а не затирать их: наружу всегда уходит
  // весь распознанный текст целиком, поэтому вызывающему достаточно
  // присвоить его в поле ввода.
  const finalTextRef = useRef('')
  // Последний «сырой» (не финализированный) текст. iOS Safari нередко отдаёт
  // распознанное только как interim и закрывает сессию без isFinal — тогда
  // при onend этот текст считается результатом, иначе поле остаётся пустым.
  const lastInterimRef = useRef('')

  // langOverride — язык конкретной записи, когда его явно выбрал ученик
  // (кнопки «Русский»/«Türkçe» в tutor-qa.tsx); без него — `lang` хука.
  const start = useCallback(
    (onFinalResult: (text: string) => void, langOverride?: string) => {
      const Ctor = window.SpeechRecognition ?? window.webkitSpeechRecognition
      if (!Ctor) return

      // Web Speech API требует secure context (HTTPS или localhost) — на
      // обычном http по локальной сети (типичный способ открыть dev-сервер
      // с телефона, см. PROJECT.md) браузер блокирует доступ к микрофону
      // независимо от того, что покажет системный диалог разрешений.
      // Без этой проверки ошибка ниже (`not-allowed`) выглядит как «нет
      // доступа», хотя на самом деле дело в схеме адреса — на опубликованном
      // https-домене та же кнопка отработает штатно.
      if (!window.isSecureContext) {
        setError('Голосовой ввод работает только по HTTPS. По обычному http (например, локальный Wi-Fi при разработке) браузер блокирует микрофон - в опубликованном приложении заработает.')
        return
      }

      const recognition = new Ctor()
      recognition.lang = langOverride ?? lang
      // Турецкая диктовка: числа, которые распознавание записало цифрами,
      // возвращаем словами (spoken-numbers-tr.ts, владелец 15.10.2026).
      const asSpoken = recognition.lang.startsWith('tr') ? spokenNumbersToTr : (t: string) => t
      recognition.interimResults = true
      // iOS Safari (Web Speech API там есть, вопреки прежней записи в
      // PROJECT.md) плохо переносит continuous: распознавание закрывается
      // сразу, не отдав результата. Для удержания это не критично — без
      // continuous сессия сама завершается на паузе, а отпускание кнопки
      // её просто останавливает, как было до этого режима.
      recognition.continuous = continuous && !IS_IOS

      recognition.onresult = (e) => {
        // Текст собирается заново из всего списка результатов сессии, а не
        // дописывается от e.resultIndex: на Android (Chrome, Яндекс) в
        // continuous-режиме resultIndex всегда 0 и финальные фразы приходят
        // повторно — дописывание давало «Merhaba Merhaba Merhaba bugün…».
        // Логика склейки и защита от повторов — в voice-transcript.ts.
        const list: { isFinal: boolean; transcript: string }[] = []
        for (let i = 0; i < e.results.length; i++) {
          const result = e.results[i]
          list.push({ isFinal: result.isFinal, transcript: result[0]?.transcript ?? '' })
        }
        const merged = mergeTranscript(list)
        const final = asSpoken(merged.final)
        const interim = asSpoken(merged.interim)
        finalTextRef.current = final
        lastInterimRef.current = interim
        // В поле показывается уже распознанное вместе с ещё «сырым» хвостом —
        // иначе при длинной фразе видна только последняя пара слов.
        setInterimText(`${final} ${interim}`.trim())
        if (final) onFinalResult(final)
      }

      recognition.onerror = (e) => {
        // 'aborted' прилетает, когда запись остановили раньше, чем браузер
        // успел начать распознавание (мгновенное нажатие-отпускание кнопки
        // микрофона) — ученику это показывать не за что.
        if (e.error === 'aborted') {
          setIsListening(false)
          return
        }
        // Коды Web Speech API — у каждого своя причина и своё лечение, особенно
        // на iPhone (Safari распознаёт через системную диктовку Apple).
        const messages: Record<string, string> = {
          'not-allowed': 'Нет доступа к микрофону. Разрешите его для сайта: "aA" в адресной строке → Настройки веб-сайта → Микрофон.',
          'service-not-allowed':
            'Распознавание речи выключено в системе. На iPhone: Настройки → Основные → Клавиатура → "Включить диктовку".',
          'no-speech': 'Ничего не расслышал - попробуйте ещё раз',
          'audio-capture': 'Микрофон не найден или занят другим приложением',
          network: 'Нет связи с сервисом распознавания - проверьте интернет',
          'language-not-supported': 'Этот язык не поддерживается распознаванием на вашем устройстве',
        }
        setError(messages[e.error] ?? `Не удалось распознать речь (код: ${e.error})`)
        console.warn('[voice] recognition error:', e.error)
        setIsListening(false)
      }

      recognition.onend = () => {
        // Сессия закрылась без финального результата, но черновой текст был —
        // отдаём его (iOS Safari). При ошибке onerror уже сработал, текста нет.
        if (!finalTextRef.current && lastInterimRef.current) {
          const final = asSpoken(mergeTranscript([{ isFinal: true, transcript: lastInterimRef.current }]).final)
          finalTextRef.current = final
          if (final) onFinalResult(final)
        }
        setIsListening(false)
        setInterimText('')
      }

      recognitionRef.current = recognition
      finalTextRef.current = ''
      lastInterimRef.current = ''
      setError(null)
      setIsListening(true)
      recognition.start()
    },
    [lang, continuous],
  )

  const stop = useCallback(() => {
    recognitionRef.current?.stop()
  }, [])

  return { isSupported, isListening, interimText, error, start, stop }
}
